import React from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle, XCircle, Banknote, Tag, Star, Handshake, ShieldCheck, Truck, Warehouse, Gift, Coins
} from 'lucide-react';

// Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const TradyFrogTrustPage: React.FC = () => {
    return (
        <article className="bg-white font-sans text-gray-800 antialiased overflow-x-hidden">
            <header className="sr-only">
                <h1>TradyFrog - India's Leading B2B Industrial E-commerce Platform</h1>
            </header>

            {/* UPDATED: Changed to max-w-8xl for wider layout */}
            <main className="max-w-8xl mx-auto py-12 px-4 sm:px-6 lg:pt-20 lg:pb-12 lg:px-8 space-y-20">
                
                {/* --- 1. Hero Section --- */}
                <motion.section 
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeInUp}
                    className="max-w-5xl mx-auto text-center"
                >
                    <span className="inline-block py-1 px-3 rounded-full bg-teal-50 text-teal-700 text-xs font-bold tracking-widest uppercase mb-4 border border-teal-100">
                        Trusted by 1.5 Lakh+ Businesses
                    </span>
                    <h1 className="text-3xl sm:text-5xl font-extrabold text-gray-900 tracking-tight mb-6 leading-tight">
                        <span className="text-teal-600 relative">TradyFrog</span> - India's Trusted Hub for <br className="hidden md:block" /> Industrial Procurement
                    </h1>
                    <div className="space-y-4 text-lg text-gray-500 leading-relaxed max-w-3xl mx-auto">
                        <p>
                            From industrial tools and medical equipment to household appliances, we bridge the gap between quality manufacturers and businesses. 
                            With <span className="font-bold text-gray-900">10,000+ verified products</span>, we stand as India's premier digital procurement solution.
                        </p>
                    </div>
                </motion.section>

                {/* --- 2. Exclusive Offers & Benefits Cards --- */}
                <section aria-labelledby="offers-heading">
                    <div className="text-center mb-12">
                        <h2 id="offers-heading" className="text-2xl sm:text-3xl font-bold text-gray-900">
                            Exclusive Benefits for You
                        </h2>
                        <div className="w-16 h-1 bg-teal-600 mx-auto mt-4 rounded-full"></div>
                    </div>
                    
                   <motion.div 
  variants={staggerContainer}
  initial="hidden"
  whileInView="visible"
  viewport={{ once: true, margin: "-50px" }}
  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
>
  <OfferCard 
    icon={<Banknote className="w-8 h-8 text-teal-600" />}
    title="GST Invoice Benefits"
    description="Save up to 18% extra on your business purchases by claiming GST input tax credits. Get proper GST invoices for all eligible orders and manage your business accounting easily."
  />

  <OfferCard 
    icon={<XCircle className="w-8 h-8 text-red-500" />}
    title="No-cost EMI"
    description="Split your purchase into easy monthly installments with zero interest on selected banks. Buy high-value products now and pay later without extra charges."
  />

  <OfferCard 
    icon={<Tag className="w-8 h-8 text-orange-500" />}
    title="Coupons & Bulk Deals"
    description="Access instant discounts on bulk orders and exclusive promotional codes on checkout. Save more when you purchase in large quantities."
  />

  <OfferCard 
    icon={<Star className="w-8 h-8 text-yellow-500 fill-yellow-500" />}
    title="Request for Quote (RFQ)"
    description="Need bulk quantities? Request a custom quote and get the best market price within 24 hours. Our team negotiates directly with sellers for you."
  />

  <OfferCard 
    icon={<Handshake className="w-8 h-8 text-blue-600" />}
    title="Flexible Payments"
    description="Pay via COD, UPI, Net Banking, Credit Card, or use credit lines like Snapmint. Choose the payment option that suits your business needs."
  />

  <OfferCard 
    icon={<ShieldCheck className="w-8 h-8 text-green-600" />}
    title="Secure & Safe"
    description="Your transactions are protected with 256-bit encryption and trusted payment gateways. Shop confidently with complete data and payment security."
  />
</motion.div>

                </section>
                
                {/* --- 3. Trust Stats & Rewards --- */}
                <section className="pt-12 border-t border-gray-100 grid lg:grid-cols-2 gap-12 items-center">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                            Why Shop at TradyFrog?
                        </h2>
                        <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                            We serve an extensive base of over <span className="font-bold text-gray-900">1,50,000+ SMEs</span> and 
                            large enterprises. With a <span className="font-bold text-teal-600">Trustpilot rating of 4.5/5</span>, 
                            our reliability is backed by verified reviews.
                        </p>
                        
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6">
                            {[
                                { icon: <CheckCircle className="w-5 h-5" />, text: "100% Genuine Products" },
                                { icon: <ShieldCheck className="w-5 h-5" />, text: "Verified Sellers Only" },
                                { icon: <Truck className="w-5 h-5" />, text: "Pan-India Delivery" },
                                { icon: <Warehouse className="w-5 h-5" />, text: "Direct Factory Pricing" }
                            ].map((item, idx) => (
                                <li key={idx} className="flex items-center gap-3 text-gray-700 font-medium p-3 bg-gray-50 rounded-lg hover:bg-teal-50 transition-colors">
                                    <span className="text-teal-600">{item.icon}</span>
                                    {item.text}
                                </li>
                            ))}
                        </ul>
                    </motion.div>

                    <motion.div 
                        whileHover={{ scale: 1.02 }}
                        className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-3xl border border-teal-100 shadow-xl relative overflow-hidden group"
                    >
                        <div className="relative z-10">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-3 bg-white rounded-full shadow-sm text-yellow-500">
                                    <Coins className="w-8 h-8" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900">TradyFrog Coins</h3>
                            </div>
                            <p className="text-gray-600 leading-relaxed mb-8">
                                Earn virtual currency with every purchase! TradyFrog Coins can be redeemed for 
                                exclusive rewards, cashback, and special gifts on your next order.
                            </p>
                            <button className="w-full bg-teal-600 text-white font-bold py-3 rounded-xl hover:bg-teal-700 transition-colors shadow-lg flex items-center justify-center gap-2">
                                <Gift className="w-5 h-5" /> Start Earning Today
                            </button>
                        </div>
                        <div className="absolute -right-10 -bottom-10 opacity-10 text-teal-600 group-hover:scale-110 transition-transform duration-500">
                            <Coins size={200} />
                        </div>
                    </motion.div>
                </section>

                {/* --- 4. NEW TEXT-HEAVY SEO SECTION (Moglix Style) --- */}
                <section className="border-t border-gray-100 pt-10 mt-10">
                    <div className="text-sm text-gray-600 space-y-8 leading-7">
                        
                        {/* Block 1: Offers */}
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 mb-3">We Have the Most Exclusive Offers and Coupons</h3>
                            <p className="mb-4">
                                In the wide realm of e-commerce, it is our mission to have happy and satisfied customers. Hence, to fulfill its aim, TradyFrog provides amazing offers and coupons. Therefore, some of the major offers are:
                            </p>
                            <ul className="list-disc pl-5 space-y-2 marker:text-gray-400">
                                <li><strong className="text-gray-800">GST invoice:</strong> Users registered under GST get an invoice to claim an additional 18% off on the order.</li>
                                <li><strong className="text-gray-800">No-cost EMI:</strong> The total price of a product is divided into months with no interest charged on them.</li>
                                <li><strong className="text-gray-800">Coupons and offers:</strong> TradyFrog provides you with various coupon codes and offers that are easily accessible on the product page.</li>
                                <li><strong className="text-gray-800">Request for quote (RFQ):</strong> In the case of out-of-stock or bulk products, buyers can easily request a quote. TradyFrog comes back to you within the next 24 hours with the best price possible.</li>
                                <li><strong className="text-gray-800">Payment options:</strong> TradyFrog offers a range of payment methods including Cash on Delivery, UPI, Wallet payments (Paytm, PhonePe), and Net Banking.</li>
                                <li><strong className="text-gray-800">Low prices:</strong> Our prices are comparatively lower than many others in the competition as we focus on providing high-worth products at affordable costs for the customers.</li>
                            </ul>
                        </div>

                        {/* Block 2: Top Selling Categories */}
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 mb-3">Some of our Top-Selling B2B Categories</h3>
                            <p className="mb-4">
                                In the expanding universe of online shopping many categories have gained popularity. TradyFrog offers a range of affordable quality products, from 1500+ categories. Some of the best-selling categories are:
                            </p>
                            <ul className="list-disc pl-5 space-y-2 marker:text-gray-400">
                                <li><strong className="text-gray-800">Home and Kitchen Appliances:</strong> In this category, we have a variety of products including geysers, air conditioners, refrigerators, fans, mixer grinders, and gas stoves.</li>
                                <li><strong className="text-gray-800">Industrial Tools:</strong> These tools help hasten your project. TradyFrog has power tools such as drills, grinders, pneumatic tools, material handling tools, and hand tools like saws, spanners, and tool kits.</li>
                                <li><strong className="text-gray-800">Electric Tools and Equipment:</strong> All electrical appliances must work at their optimum levels. We have wires, circuit breakers, fuses, switches, generators, solar inverters, and much more.</li>
                                <li><strong className="text-gray-800">Safety Supplies:</strong> For every supervisor, safety plays a pivotal role. The best-in-class safety products at TradyFrog are safety shoes, helmets, workwear, gloves, and traffic safety gear.</li>
                                <li><strong className="text-gray-800">Office Supplies:</strong> Your office should be as ideal as your home. Get top-quality printers, office chairs, currency-counting machines, barcode scanners, and stationery.</li>
                                <li><strong className="text-gray-800">Construction Material:</strong> We supply high-grade cement, TMT bars, plumbing fittings, bathroom fixtures, and electrical wiring to ensure durability.</li>
                            </ul>
                        </div>

                        {/* Block 3: Why Us */}
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 mb-3">Why do Industrial Shopping Online from TradyFrog?</h3>
                            <p>
                                TradyFrog has an extensive base of over <strong className="text-gray-800">1,50,000 SMEs</strong> and large enterprises across India. Over <strong className="text-gray-800">10,000+ sellers</strong> upload products to the website. There are <strong className="text-gray-800">8,00,000+ products</strong> available. We have a Trustpilot rating of 4.5 out of 5 stars. TradyFrog also offers nationwide delivery to more than <strong className="text-gray-800">25,000+ PIN codes</strong>. We aim to provide great value to customers with popular brands and secure payment partners.
                            </p>
                        </div>

                        {/* Block 4: Exclusive Features */}
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 mb-3">Take Advantage of Exclusive Features at TradyFrog.com</h3>
                            <p className="mb-4">
                                We also have a wide range of features given to users for a seamless online shopping experience including order tracking. TradyFrog even gives bulk-order inquiry options for users who want to buy products for their business organizations and can claim huge discounts. We also provide product cancellation, a smart return policy, and a replacement process.
                            </p>
                            <p>
                                <strong className="text-gray-800">TradyFrog App</strong> - With the TradyFrog App customers get exclusive deals, faster checkouts, and personalized recommendations! Now enjoy only-in-app benefits, effective product browsing, and stay connected anytime, anywhere.
                            </p>
                        </div>

                        {/* Footer Disclaimer */}
                        <div className="pt-4 border-t border-gray-100 text-xs text-gray-400">
                            Other than all the top-selling categories mentioned above, we also deal in smartphones, pumps and motors, LED lights, plumbing, solar equipment, and packaging materials. All products are genuine and priced for affordability.
                        </div>

                    </div>
                </section>

            </main>
        </article>
    );
};

// Sub-component for individual Offer Cards
const OfferCard: React.FC<{ icon: React.ReactNode, title: string, description: string }> = ({ icon, title, description }) => (
    <motion.div 
        variants={fadeInUp}
        whileHover={{ y: -5 }}
        className="flex flex-col p-6 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-teal-200 transition-all duration-300 group"
    >
        <div className="mb-4 p-3 bg-gray-50 rounded-xl w-fit group-hover:bg-white group-hover:shadow-md transition-all">
            {icon}
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-700 transition-colors">{title}</h3>
        <p className="text-sm text-gray-500 leading-relaxed">{description}</p>
    </motion.div>
);

export default TradyFrogTrustPage;